import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { AppProviders } from "./app/providers/AppProviders";
import { router } from "./app/router/router";

// Variable + static font faces. Loaded eagerly so the first paint uses the
// brand typefaces — the alternative (CDN <link>) FOIT-flickers between
// system-ui and Inter Tight on slow connections, which feels broken in a
// design-forward product. Both packages self-host the woff2 files.
import "@fontsource-variable/inter-tight/wght.css";
import "@fontsource/jetbrains-mono/400.css";
import "@fontsource/jetbrains-mono/500.css";
import "@fontsource/jetbrains-mono/600.css";

// Mermaid is lazy-loaded on first <MermaidDiagram /> mount — see
// lib/mermaid.ts. Doing it here as an eager import was costing every
// pageview ~250 KB of gzipped JS even on routes that never render a
// diagram (login, projects list, history, settings).

// Self-heal stale tabs after a deploy: when a dynamically imported chunk
// 404s because its content hash no longer exists on the server, reload.
// The 10s window coalesces back-to-back failures so a genuinely broken
// asset can't drive a reload loop, while still letting a long-lived tab
// recover from a second deploy later in the same session.
window.addEventListener("vite:preloadError", () => {
  const last = Number(sessionStorage.getItem("preload-reloaded-at") ?? 0);
  if (Date.now() - last < 10_000) return;
  sessionStorage.setItem("preload-reloaded-at", String(Date.now()));
  window.location.reload();
});

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element not found");

createRoot(rootEl).render(
  <StrictMode>
    <AppProviders>
      <RouterProvider router={router} />
    </AppProviders>
  </StrictMode>,
);
